import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import PublicHeader from '../../components/navigation/PublicHeader';
import Footer from '../marketing-landing/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ExecutiveCard from './components/ExecutiveCard';
import FilterBar from './components/FilterBar';
import StatsPanel from './components/StatsPanel';
import { useLanguage } from '../../contexts/LanguageContext';

const ExecutivePreviewGallery = () => {
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const { t, language } = useLanguage();

  // Function to get translated profile data
  const getTranslatedExecutives = () => {
    const baseExecutives = [
      {
        id: 1,
        titleKey: 'ceo',
        experience: '15+',
        industriesKeys: ['technology', 'saas'],
        skillsKeys: ['strategicPlanning', 'plManagement', 'boardRelations', 'ma', 'fundraising'],
        achievementsKeys: ['ceo1', 'ceo2', 'ceo3'],
        descriptionKey: 'ceo',
        matchScore: 98,
        availabilityKey: 'openToOpportunities',
        companyScaleKey: 'employees200Plus'
      },
      {
        id: 2,
        titleKey: 'cfo',
        experience: '12+',
        industriesKeys: ['finance', 'fintech'],
        skillsKeys: ['financialStrategy', 'ipoExperience', 'riskManagement', 'fpa', 'treasury'],
        achievementsKeys: ['cfo1', 'cfo2', 'cfo3'],
        descriptionKey: 'cfo',
        matchScore: 95,
        availabilityKey: 'selective',
        companyScaleKey: 'employees500Plus'
      },
      {
        id: 3,
        titleKey: 'itDirector',
        experience: '10+',
        industriesKeys: ['technology', 'healthcare'],
        skillsKeys: ['itInfrastructure', 'cybersecurity', 'cloudMigration', 'teamLeadership', 'vendorManagement'],
        achievementsKeys: ['it1', 'it2', 'it3'],
        descriptionKey: 'itDirector',
        matchScore: 92,
        availabilityKey: 'openToOpportunities',
        companyScaleKey: 'employees300to500'
      },
      {
        id: 4,
        titleKey: 'projectDirector',
        experience: '11+',
        industriesKeys: ['construction', 'realEstate'],
        skillsKeys: ['projectManagement', 'budgetControl', 'stakeholderManagement', 'riskMitigation', 'qualityAssurance'],
        achievementsKeys: ['project1', 'project2', 'project3'],
        descriptionKey: 'projectDirector',
        matchScore: 89,
        availabilityKey: 'selective',
        companyScaleKey: 'employees150to200'
      },
      {
        id: 5,
        titleKey: 'seniorDeveloper',
        experience: '9+',
        industriesKeys: ['technology', 'ecommerce'],
        skillsKeys: ['fullStackDevelopment', 'systemArchitecture', 'codeReview', 'mentoring', 'agileScrum'],
        achievementsKeys: ['dev1', 'dev2', 'dev3'],
        descriptionKey: 'seniorDeveloper',
        matchScore: 94,
        availabilityKey: 'openToOpportunities',
        companyScaleKey: 'employees100to150'
      },
      {
        id: 6,
        titleKey: 'chiefAccountant',
        experience: '13+',
        industriesKeys: ['finance', 'manufacturing'],
        skillsKeys: ['financialReporting', 'ifrsGaap', 'auditManagement', 'taxCompliance', 'erpSystems'],
        achievementsKeys: ['accountant1', 'accountant2', 'accountant3'],
        descriptionKey: 'chiefAccountant',
        matchScore: 91,
        availabilityKey: 'selective',
        companyScaleKey: 'employees250to300'
      },
      {
        id: 7,
        titleKey: 'hrDirector',
        experience: '12+',
        industriesKeys: ['humanResources', 'technology'],
        skillsKeys: ['talentAcquisition', 'organizationalDevelopment', 'employeeRelations', 'compensationStrategy', 'changeManagement'],
        achievementsKeys: ['hr1', 'hr2', 'hr3'],
        descriptionKey: 'hrDirector',
        matchScore: 93,
        availabilityKey: 'openToOpportunities',
        companyScaleKey: 'employees400Plus'
      },
      {
        id: 8,
        titleKey: 'realEstateDirector',
        experience: '14+',
        industriesKeys: ['realEstate', 'investment'],
        skillsKeys: ['portfolioManagement', 'assetValuation', 'leaseNegotiation', 'marketAnalysis', 'investmentStrategy'],
        achievementsKeys: ['realEstate1', 'realEstate2', 'realEstate3'],
        descriptionKey: 'realEstateDirector',
        matchScore: 88,
        availabilityKey: 'selective',
        companyScaleKey: 'employees50to100'
      },
      {
        id: 9,
        titleKey: 'vpConstruction',
        experience: '16+',
        industriesKeys: ['construction', 'infrastructure'],
        skillsKeys: ['constructionManagement', 'safetyCompliance', 'contractNegotiation', 'teamLeadership', 'costEstimation'],
        achievementsKeys: ['construction1', 'construction2', 'construction3'],
        descriptionKey: 'vpConstruction',
        matchScore: 90,
        availabilityKey: 'openToOpportunities',
        companyScaleKey: 'employees500Plus'
      }
    ];

    // Transform to translated data
    return baseExecutives?.map(exec => ({
      id: exec?.id,
      title: t(`profiles.titles.${exec?.titleKey}`),
      experience: `${exec?.experience} ${t('preview.experience')}`,
      industries: exec?.industriesKeys?.map(key => t(`profiles.industries.${key}`)),
      skills: exec?.skillsKeys?.map(key => t(`profiles.skills.${key}`)),
      achievements: exec?.achievementsKeys?.map(key => t(`profiles.achievements.${key}`)),
      description: t(`profiles.descriptions.${exec?.descriptionKey}`),
      matchScore: exec?.matchScore,
      availability: t(`profiles.availability.${exec?.availabilityKey}`),
      companyScale: t(`profiles.companyScale.${exec?.companyScaleKey}`)
    }));
  };

  const executives = getTranslatedExecutives();

  const filteredExecutives = executives?.filter(exec => {
    const roleMatch = selectedRole === 'all' || exec?.title?.toLowerCase()?.includes(selectedRole?.toLowerCase());
    const industryMatch = selectedIndustry === 'all' || exec?.industries?.some(ind => ind?.toLowerCase()?.includes(selectedIndustry?.toLowerCase()));
    return roleMatch && industryMatch;
  });

  return (
    <>
      <Helmet>
        <title>{t('preview.title')} - BlinD</title>
        <meta 
          name="description" 
          content={t('preview.mainSubtitle')} 
        />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
        <PublicHeader />
        
        <main className="pt-24 pb-16">
          {/* Hero Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 mb-12">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-xl border border-white/20 shadow-lg rounded-lg mb-4">
                <Icon name="Users" size={20} color="#0025cc" />
                <span className="text-sm font-medium text-[#0025cc]">{t('preview.badge')}</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#111827] mb-4">
                {t('preview.mainTitle')}
              </h1>
              <p className="text-lg text-[#6B7280] max-w-3xl mx-auto">
                {t('preview.mainSubtitle')}
              </p>
            </div>

            <StatsPanel totalExecutives={executives?.length} />
          </section>

          {/* Filter Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 mb-8">
            <FilterBar 
              selectedRole={selectedRole}
              selectedIndustry={selectedIndustry}
              onRoleChange={setSelectedRole}
              onIndustryChange={setSelectedIndustry}
            />
          </section>

          {/* Executive Cards Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
              {filteredExecutives?.map((executive) => (
                <ExecutiveCard key={executive?.id} executive={executive} />
              ))}
            </div>

            {/* CTA Section */}
            <div className="bg-white/40 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-12 text-center shadow-xl">
              <div className="max-w-2xl mx-auto">
                <Icon name="Target" size={48} color="#0025cc" className="mx-auto mb-4" />
                <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-4">
                  {t('preview.ctaTitle')}
                </h2>
                <p className="text-lg text-[#6B7280] mb-6">
                  {t('preview.ctaSubtitle')}
                </p>
                <Button 
                  variant="default" 
                  size="lg"
                  onClick={() => window.location.href = '/access-request'}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  {t('preview.ctaButton')}
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ExecutivePreviewGallery;